import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../pages/AuthContext';
import { IoEye, IoEyeOff } from "react-icons/io5";
import { API_END_POINT } from '../pages/Utils/Constant';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logoimg from "../assets/rootimg/astrotellerlogo.png";
import CommonNavbar from '../pages/CommonNavbar';

const VerifyEmail = () => {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
    const navigate = useNavigate();
    const { setIsLogged } = useContext(AuthContext);
    const email = sessionStorage.getItem('tempEmail');

    // Redirect if no email in session
    useEffect(() => {
        if (!email) {
            navigate('/signup');
        }
    }, [email, navigate]);

    // Countdown timer
    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');

        try {
            const response = await axios.post(`${API_END_POINT}auth/verify-otp`, {
                email,
                otp
            });

            // Store the JWT token
            localStorage.setItem('authToken', response.data.token);

            // Store user session data
            const userData = {
                email: email,
                isLoggedIn: true,
                loginTime: new Date().toISOString()
            };
            localStorage.setItem('userSession', JSON.stringify(userData));

            // Set user as logged in
            setIsLogged(true);

            // Show success toast
            toast.success('Email verified successfully!', {
                position: "top-center",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                onClose: () => {
                    // Clear temporary email from session storage
                    sessionStorage.removeItem('tempEmail');
                    // Navigate to home page after toast closes
                    navigate('/');
                }
            });

        } catch (error) {
            console.error('OTP Verification error:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
            setErrorMessage(error.response?.data?.message || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            await axios.post(`${API_END_POINT}auth/signup`, {
                email
            });

            // Reset countdown
            setCountdown(300);
            toast.info('New OTP sent to your email!');

        } catch (error) {
            toast.error('Failed to resend OTP. Please try again.');
            setErrorMessage('Failed to resend OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-white to-success/40">
            <CommonNavbar />
            <ToastContainer />
            <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
                {/* Logo */}
                <div className="flex justify-center mb-2">
                    <div className="w-[75%] h-auto mx-auto">
                        <img src={logoimg} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                </div>

                {/* Heading */}
                <h1 className="text-lg font-semibold text-center text-secondary">
                Unlock Your Cosmic Journey
                </h1>
                <p className="text-gray-600 text-center mb-4">
                    Please verify your email
                </p>

                <div className="text-center text-gray-600 mb-4">
                    Verification code sent to<br />
                    <span className="font-medium">{email}</span>
                </div>

                <form onSubmit={handleVerify} className="space-y-6">
                    <div>
                        <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="Enter verification code"
                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#6366f1] text-center"
                            maxLength="6"
                        />
                    </div>

                    {errorMessage && (
                        <p className="text-red-500 text-center text-sm">{errorMessage}</p>
                    )}

                    <div className="text-center text-gray-600 text-sm">
                        Time remaining: {formatTime(countdown)}
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !otp || otp.length !== 6}
                        className="w-full bg-black text-white py-3 rounded-md font-medium hover:bg-secondary transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {loading ? "Verifying..." : "Verify Email"}
                    </button>

                    {countdown === 0 && (
                        <button
                            type="button"
                            onClick={handleResendOTP}
                            disabled={loading}
                            className="w-full text-blue-500 font-medium hover:text-blue-600 transition-colors"
                        >
                            Resend Code
                        </button>
                    )}
                </form>
            </div>
        </div>
    );
};

export default VerifyEmail;